import React from 'react';
import {
  Autocomplete,
  TextField,
  TextFieldProps,
  SxProps,
  Theme,
  Box,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { createFieldTestId } from '../utils/testIds';

export interface TextMultiAutocompleteProps<T> {
  /** Array of options to display in the dropdown */
  options: T[];
  /** Currently selected values */
  value: T[];
  /** Callback when selection changes */
  onChange: (value: T[]) => void;
  /** Function to get display label from option (default: String conversion) */
  getOptionLabel?: (option: T) => string;
  /** Function to get unique key from option (default: uses option itself) */
  getOptionKey?: (option: T) => string | number;
  /** Label for the input field */
  label?: string;
  /** Placeholder text when no selection */
  placeholder?: string;
  /** Controlled input value for search text */
  inputValue?: string;
  /** Callback when input value changes */
  onInputChange?: (value: string) => void;
  /** Minimum character width reserved for typing (default: 6) */
  minInputChars?: number;
  /** Fixed height of the component in pixels (default: 40) */
  height?: number;
  /** Custom function to render selected values as text (default: comma-separated) */
  renderSelectedText?: (selected: T[]) => string;
  /** Additional MUI sx props */
  sx?: SxProps<Theme>;
  /** Additional TextField props */
  textFieldProps?: Partial<TextFieldProps>;
  /** Disabled state */
  disabled?: boolean;
  /** Error state */
  error?: boolean;
  /** Helper text */
  helperText?: string;
  /** Keep dropdown open when selecting options (default: true) */
  disableCloseOnSelect?: boolean;
  /** Test ID for the component */
  'data-testid'?: string;
  /** Name for creating test IDs */
  name?: string;
}

/**
 * A multi-select autocomplete component that displays selected values as plain text
 * instead of chips, maintaining a fixed single-line height.
 *
 * Features:
 * - Fixed height (no wrapping)
 * - Selected values shown as comma-separated text with ellipsis overflow
 * - Always reserves space for typing
 * - Proper spacing for clear button and dropdown icon
 * - Fully accessible and keyboard-navigable
 *
 * @example
 * ```tsx
 * const [selected, setSelected] = useState<string[]>([]);
 * const [searchText, setSearchText] = useState('');
 *
 * <TextMultiAutocomplete
 *   options={['Option A', 'Option B', 'Option C']}
 *   value={selected}
 *   onChange={setSelected}
 *   inputValue={searchText}
 *   onInputChange={setSearchText}
 *   label="Select Items"
 *   placeholder="Type to search..."
 * />
 * ```
 */
export function TextMultiAutocomplete<T>({
  options,
  value,
  onChange,
  getOptionLabel = (option) => String(option),
  getOptionKey,
  label,
  placeholder,
  inputValue,
  onInputChange,
  minInputChars = 6,
  height = 40,
  renderSelectedText,
  sx,
  textFieldProps,
  disabled = false,
  error = false,
  helperText,
  disableCloseOnSelect = true,
  'data-testid': testId,
  name,
}: TextMultiAutocompleteProps<T>) {
  // Default render function: comma-separated labels
  const defaultRenderSelectedText = (selected: T[]): string => {
    return selected.map(getOptionLabel).join(', ');
  };

  const selectedTextRenderer = renderSelectedText || defaultRenderSelectedText;
  const selectedText = value.length > 0 ? selectedTextRenderer(value) : '';

  // Determine if we should show the placeholder
  const showPlaceholder = value.length === 0 && !inputValue;
  
  // Generate test ID
  const autocompleteTestId = testId || (name ? createFieldTestId(`autocomplete-${name}`) : createFieldTestId('autocomplete-multi'));
  const textfieldTestId = testId ? `${testId}-input` : (name ? createFieldTestId(`autocomplete-input-${name}`) : createFieldTestId('autocomplete-input'));

  return (
    <Autocomplete
      data-testid={autocompleteTestId}
      multiple
      disableCloseOnSelect={disableCloseOnSelect}
      options={options}
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      inputValue={inputValue}
      onInputChange={(_, newInputValue, reason) => {
        // Only update input value for user input, not for reset
        if (reason === 'input' && onInputChange) {
          onInputChange(newInputValue);
        } else if (reason === 'clear' && onInputChange) {
          onInputChange('');
        }
      }}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, value) => {
        if (getOptionKey) {
          return getOptionKey(option) === getOptionKey(value);
        }
        return option === value;
      }}
      filterSelectedOptions
      disabled={disabled}
      // Render options with checkboxes
      renderOption={(props, option, { selected }) => {
        const { key, ...otherProps } = props;
        return (
          <li key={key} {...otherProps} data-testid={`${autocompleteTestId}-option-${getOptionKey ? getOptionKey(option) : getOptionLabel(option)}`}>
            <Checkbox style={{ marginRight: 8 }} checked={selected} data-testid={`${autocompleteTestId}-checkbox-${getOptionKey ? getOptionKey(option) : getOptionLabel(option)}`} />
            <ListItemText primary={getOptionLabel(option)} />
          </li>
        );
      }}
      // Do NOT use renderTags - it's for chips
      // Use slotProps to customize the input rendering
      slotProps={{
        paper: {
          sx: {
            // Ensure dropdown renders nicely
            '& .MuiAutocomplete-listbox': {
              maxHeight: '300px',
            },
          },
        },
      }}
      sx={{
        ...sx,
        // Ensure the root container respects height
        '& .MuiOutlinedInput-root': {
          height: `${height}px`,
          flexWrap: 'nowrap',
          alignItems: 'center',
          paddingRight: '65px', // Reserve space for clear + dropdown icons
          paddingTop: 0,
          paddingBottom: 0,
          
          // The input tag container
          '& .MuiAutocomplete-input': {
            flexGrow: 1,
            minWidth: `${minInputChars}ch`,
            padding: '0 4px',
          },
        },
      }}
      renderInput={(params) => {
        // Create a custom input that shows selected text + typing area
        return (
          <TextField
            {...params}
            {...textFieldProps}
            data-testid={textfieldTestId}
            label={label}
            placeholder={showPlaceholder ? placeholder : undefined}
            error={error}
            helperText={helperText}
            InputProps={{
              ...params.InputProps,
              ...textFieldProps?.InputProps,
              // Render selected text before the input
              startAdornment: selectedText ? (
                <Box
                  component="span"
                  data-testid={`${autocompleteTestId}-selected-text`}
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flexShrink: 1,
                    paddingLeft: '14px',
                    paddingRight: '4px',
                    color: 'text.primary',
                    fontSize: '1rem',
                    lineHeight: `${height}px`,
                  }}
                >
                  {selectedText}
                </Box>
              ) : null,
              // Keep the end adornment (clear + dropdown icons)
              endAdornment: params.InputProps.endAdornment,
            }}
          />
        );
      }}
    />
  );
}

export default TextMultiAutocomplete;
